import { datasetVersion, datasetChoices, lookupDataset } from "./datasets";
import {
  formattedPolicyValue,
  policyValueError,
  numericValue,
  strictDate,
  strictDateTime,
  strictTime,
} from "./field-policies";
import {
  evaluateCalculation,
  calculationDependencies,
  CalculationError,
} from "./calculations";
import { addressParts, mapProvenanceSchema } from "./maps";
import { presetDependencies, presetTarget, presetWritable } from "./presets";
import { damReceiptSchema } from "./dam";
import { backgroundRemovalSchema } from "./image-background";
import { selectStatement, validateStatementSelection } from "./statements";
import {
  dynamicSourceCanSkip,
  requestedDynamicSources,
} from "./dynamic-parameters";
import {
  parseDynamicChoiceCatalog,
  isDynamicChoiceField,
} from "./dynamic-choices";
import {
  type Project,
  type Field,
  type Values,
  type Value,
  type Condition,
  fieldSchema,
  adjustmentSchema,
  defaultAdjustments,
  safePattern,
} from "./schema";
export const isEmpty = (v: unknown): boolean =>
  v == null ||
  v === "" ||
  (Array.isArray(v) && !v.length) ||
  (typeof v === "string" && !v.trim()) ||
  (!!v &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    Object.hasOwn(v, "statement") &&
    typeof (v as Record<string, unknown>).text === "string" &&
    !String((v as Record<string, unknown>).text).trim());
export const displayValue = (v: unknown): string =>
  v == null
    ? ""
    : Array.isArray(v)
      ? v.join(", ")
      : typeof v === "object"
        ? Object.values(v)
            .filter((v) => typeof v === "string")
            .join(", ")
        : String(v);
export function condition(c: Condition, values: Values): boolean {
  if (c.all) return c.all.every((x) => condition(x, values));
  if (c.any) return c.any.some((x) => condition(x, values));
  const v = values[c.field!],
    s = displayValue(v),
    target = displayValue(c.value);
  switch (c.op) {
    case "eq":
      return Array.isArray(v)
        ? v.includes(target)
        : v === c.value || s === target;
    case "neq":
      return !(Array.isArray(v)
        ? v.includes(target)
        : v === c.value || s === target);
    case "empty":
      return isEmpty(v);
    case "notEmpty":
      return !isEmpty(v);
    case "startsWith":
      return s.startsWith(target);
    case "endsWith":
      return s.endsWith(target);
    case "contains":
      return s.includes(target);
    case "notContains":
      return !s.includes(target);
    case "matches":
      return safePattern(target).test(s.slice(0, 32768));
    case "gt":
      return Number(v) > Number(c.value);
    case "lt":
      return Number(v) < Number(c.value);
    case "in":
      return Array.isArray(c.value) && c.value.map(String).includes(s);
    default:
      return false;
  }
}
const ownSource = (map: Values | undefined, key: string): Value | undefined =>
  map && Object.hasOwn(map, key) ? map[key] : undefined;
export interface Context {
  sourceParameters?: import("./schema").SourceParameters;
  user?: Values;
  order?: Values;
  forms?: Record<string, Values>;
  dynamic?: Values;
  now?: string;
}
export function effectiveFields(
  project: Project,
  partId?: string,
  context: Context = {},
): Field[] {
  const part = project.parts.find((x) => x.id === partId);
  return project.fields
    .map((f) => {
      const patch = part?.overrides[f.id];
      const result = fieldSchema.parse({
        ...f,
        ...patch,
        id: f.id,
        key: f.key,
      });
      if (result.source.kind === "dataset")
        result.choices = datasetChoices(
          datasetVersion(project, result.source),
          result.source,
        );
      if (result.source.kind === "lookup") {
        result.editable = false;
        result.choices = [];
      }
      if (result.calculation) result.editable = false;
      if (
        result.source.kind === "dynamic" &&
        result.source.dynamicMode === "choices" &&
        !(
          result.source.dynamicSourceId &&
          context.sourceParameters?.[result.source.dynamicSourceId] === null
        )
      ) {
        const raw = ownSource(context.dynamic, result.source.key || result.key);
        if (raw !== undefined)
          result.choices = parseDynamicChoiceCatalog(raw).choices;
      }
      return result;
    })
    .sort((a, b) => a.order - b.order);
}
export function setPartOverride(
  project: Project,
  partId: string,
  fieldId: string,
  patch: Partial<Field> | null,
): Project {
  const p = structuredClone(project);
  const part = p.parts.find((x) => x.id === partId);
  if (!part) throw new Error("Part not found");
  if (!p.fields.some((f) => f.id === fieldId))
    throw new Error("Field not found");
  if (patch === null) delete part.overrides[fieldId];
  else part.overrides[fieldId] = { ...part.overrides[fieldId], ...patch };
  return p;
}
/** An explicit reset removes a metadata-only variable when its last configured delta is cleared. */
export function clearPartOverrideProperty(
  project: Project,
  partId: string,
  fieldId: string,
  property: keyof Field,
): Project {
  const p = structuredClone(project),
    part = p.parts.find((item) => item.id === partId);
  if (!part) throw new Error("Part not found");
  if (!p.fields.some((field) => field.id === fieldId))
    throw new Error("Field not found");
  const patch = part.overrides[fieldId];
  if (!patch) return p;
  delete patch[property];
  if (!Object.keys(patch).some((key) => key !== "legacy"))
    delete part.overrides[fieldId];
  return p;
}
export interface Resolution {
  calculationErrors?: Diagnostic[];
  fields: Field[];
  values: Values;
  visible: Set<string>;
  choices: Record<string, Field["choices"]>;
  groups: Set<string>;
  price: number;
  proofName: string;
}
export function resolve(
  project: Project,
  input: Values = {},
  partId?: string,
  ctx: Context = {},
): Resolution {
  const fields = effectiveFields(project, partId, ctx),
    part = project.parts.find((x) => x.id === partId),
    values: Values = {};
  const visible = new Set<string>(),
    groups = new Set(project.groups.filter((g) => g.visible).map((g) => g.id));
  const choices: Resolution["choices"] = {};
  const calculationErrors = new Map<string, Diagnostic>();
  const calculate = (field: Field, available: Values) => {
    if (!field.calculation) return;
    try {
      values[field.id] = evaluateCalculation(
        field.calculation,
        available,
        condition,
        displayValue,
      );
      calculationErrors.delete(field.id);
    } catch (error) {
      if (!(error instanceof CalculationError)) throw error;
      values[field.id] = null;
      calculationErrors.set(field.id, {
        fieldId: field.id,
        code: "CALCULATION_LIMIT",
        message: `${field.label}: ${error.message}`,
        severity: "error",
      });
    }
  };
  for (const f of fields) {
    let value: Value = f.default;
    switch (f.source.kind) {
      case "user":
        value = ownSource(ctx.user, f.source.key || f.key) ?? value;
        break;
      case "order":
        value = ownSource(ctx.order, f.source.key || f.key) ?? value;
        break;
      case "form":
        value =
          ownSource(
            ctx.forms && Object.hasOwn(ctx.forms, f.source.instanceId || "")
              ? ctx.forms[f.source.instanceId || ""]
              : undefined,
            f.source.structId || "",
          ) ?? value;
        break;
      case "statement":
        if (f.source.datasetId && f.source.statementId)
          value = selectStatement(project, f, f.source.statementId);
        break;
      case "dynamic":
        {
          if (
            f.source.dynamicSourceId &&
            ctx.sourceParameters?.[f.source.dynamicSourceId] === null
          )
            break;
          const raw = ownSource(ctx.dynamic, f.source.key || f.key);
          if (isDynamicChoiceField(f)) {
            if (raw !== undefined) {
              const catalog = parseDynamicChoiceCatalog(raw);
              if (Object.hasOwn(catalog, "value")) value = catalog.value!;
            }
          } else value = raw ?? value;
        }
        break;
    }
    if (part && f.id in part.systemValues) value = part.systemValues[f.id];
    if (f.autoGenerated) value = ctx.now || "AUTO";
    if (f.editable && !f.system && f.id in input) value = input[f.id];
    values[f.id] = value;
    choices[f.id] =
      f.type === "preset"
        ? f.presets.map((record) => ({ value: record.id, label: record.label }))
        : f.choices;
  }
  const initialValues = { ...values },
    clearedByTrigger = new Set<string>();
  const presetEdges = presetDependencies(fields);
  const evaluating = new Set<string>(),
    evaluated = new Set<string>();
  const dependencies = (c: Condition): string[] =>
    c.field ? [c.field] : (c.all || c.any || []).flatMap(dependencies);
  const evaluate = (f: Field) => {
    if (evaluated.has(f.id)) return;
    if (evaluating.has(f.id)) throw new Error("Visibility cycle");
    evaluating.add(f.id);
    for (const id of [
      ...(presetEdges.get(f.id) || []),
      ...(f.visibleWhen ? dependencies(f.visibleWhen) : []),
      ...(f.source.kind === "lookup" ? [f.source.lookupFieldId!] : []),
      ...(f.calculation ? calculationDependencies(f.calculation) : []),
    ]) {
      const ref = fields.find((f) => f.id === id);
      if (ref) evaluate(ref);
    }
    if (presetWritable(f) && !Object.hasOwn(input, f.id)) {
      for (const selectorId of presetEdges.get(f.id) || []) {
        if (!visible.has(selectorId)) continue;
        const selector = fields.find((field) => field.id === selectorId)!;
        if (selector.type !== "preset") continue;
        const record =
          selector.presets.find(
            (record) => record.id === String(values[selectorId] ?? ""),
          ) ||
          selector.presets.find(
            (record) =>
              record.match &&
              visible.has(record.match.field) &&
              condition(record.match, values),
          );
        for (const [key, value] of Object.entries(record?.values || {}))
          if (presetTarget(fields, key).id === f.id) values[f.id] = value;
      }
    }
    if (f.source.kind === "lookup")
      values[f.id] = lookupDataset(project, f, values, visible);
    if (f.calculation) {
      const available = { ...values };
      for (const reference of calculationDependencies(f.calculation))
        if (!visible.has(reference)) available[reference] = null;
      calculate(f, available);
    }
    const matches = f.visibleWhen ? condition(f.visibleWhen, values) : true;
    const show =
      !f.hidden &&
      (!f.visibleWhen ||
        (f.visibilityAction === "hide" ? !matches : matches)) &&
      (!f.groupId || groups.has(f.groupId));
    if (show) visible.add(f.id);
    else if (f.visibleWhen) values[f.id] = null;
    evaluated.add(f.id);
    evaluating.delete(f.id);
  };
  fields.forEach(evaluate);
  const expand = (id: string) =>
    project.groups.some((g) => g.id === id)
      ? fields.filter((f) => f.groupId === id).map((f) => f.id)
      : [id];
  for (const rule of project.triggers) {
    const source = values[rule.sourceId];
    if (
      !visible.has(rule.sourceId) ||
      !condition(
        { field: rule.sourceId, op: rule.op || "eq", value: rule.value },
        values,
      )
    )
      continue;
    for (const target of rule.show) {
      if (groups.has(target) || project.groups.some((g) => g.id === target))
        groups.add(target);
      expand(target).forEach((id) => visible.add(id));
    }
    for (const target of [...rule.hide, ...rule.clear]) {
      groups.delete(target);
      expand(target).forEach((id) => {
        visible.delete(id);
        if (rule.clear.includes(target)) {
          values[id] = null;
          clearedByTrigger.add(id);
        }
      });
    }
    for (const [id, excluded] of Object.entries(rule.exclude)) {
      choices[id] =
        choices[id]?.filter((x) => !excluded.includes(x.value)) || [];
      if (excluded.includes(String(values[id]))) {
        values[id] = null;
        clearedByTrigger.add(id);
      }
    }
  }
  // Downstream conditional fields must not read a hidden source's retained value.
  const conditionValues = { ...values };
  for (const f of fields) if (!visible.has(f.id)) conditionValues[f.id] = null;
  const refresh = (f: Field, visited = new Set<string>()) => {
    if (visited.has(f.id)) return;
    visited.add(f.id);
    for (const id of [
      ...(presetEdges.get(f.id) || []),
      ...(f.visibleWhen ? dependencies(f.visibleWhen) : []),
      ...(f.calculation ? calculationDependencies(f.calculation) : []),
    ]) {
      const ref = fields.find((x) => x.id === id);
      if (ref) refresh(ref, visited);
    }
    if (
      presetEdges.has(f.id) &&
      presetWritable(f) &&
      !Object.hasOwn(input, f.id) &&
      !clearedByTrigger.has(f.id)
    ) {
      values[f.id] = initialValues[f.id];
      for (const selectorId of presetEdges.get(f.id) || []) {
        if (!visible.has(selectorId)) continue;
        const selector = fields.find((field) => field.id === selectorId)!;
        if (selector.type !== "preset") continue;
        const record =
          selector.presets.find(
            (record) => record.id === String(values[selectorId] ?? ""),
          ) ||
          selector.presets.find(
            (record) =>
              record.match &&
              visible.has(record.match.field) &&
              condition(record.match, values),
          );
        for (const [key, value] of Object.entries(record?.values || {}))
          if (presetTarget(fields, key).id === f.id) values[f.id] = value;
      }
      conditionValues[f.id] = visible.has(f.id) ? values[f.id] : null;
    }
    if (f.source.kind === "lookup") {
      const ref = fields.find((x) => x.id === f.source.lookupFieldId);
      if (ref) refresh(ref, visited);
      values[f.id] = lookupDataset(project, f, values, visible);
      conditionValues[f.id] = visible.has(f.id) ? values[f.id] : null;
    }
    if (f.calculation && !clearedByTrigger.has(f.id)) {
      calculate(f, conditionValues);
      conditionValues[f.id] = visible.has(f.id) ? values[f.id] : null;
    }
    if (f.visibleWhen) {
      const match = condition(f.visibleWhen, conditionValues);
      if (f.visibilityAction === "hide" ? match : !match) {
        visible.delete(f.id);
        values[f.id] = null;
        conditionValues[f.id] = null;
      }
    }
  };
  fields.forEach((f) => refresh(f));
  return {
    ...(calculationErrors.size
      ? { calculationErrors: [...calculationErrors.values()] }
      : {}),
    fields,
    values,
    visible,
    choices,
    groups,
    price:
      Math.round(
        fields.reduce(
          (sum, f) => sum + (!isEmpty(values[f.id]) ? f.price : 0),
          0,
        ) * 10000,
      ) / 10000,
    proofName: fields
      .filter((f) => f.proofName)
      .map((f) => displayValue(values[f.id]))
      .filter(Boolean)
      .join(" · "),
  };
}
export interface Diagnostic {
  fieldId?: string;
  nodeId?: string;
  pageId?: string;
  code: string;
  message: string;
  severity: "error" | "warning";
}
export function validateSubmission(
  project: Project,
  input: Values,
  partId?: string,
  ctx: Context = {},
): { resolution: Resolution; errors: Diagnostic[]; snapshot: Values } {
  const resolution = resolve(project, input, partId, ctx),
    errors: Diagnostic[] = [...(resolution.calculationErrors || [])],
    snapshot: Values = {};
  for (const source of requestedDynamicSources(project, partId))
    if (
      ctx.sourceParameters?.[source.id] === null &&
      !dynamicSourceCanSkip(project, source.id, partId)
    )
      errors.push({
        code: "SOURCE_REQUIRED",
        message: `${source.name}: this source is required`,
        severity: "error",
        fieldId: resolution.fields.find(
          (field) => field.source.dynamicSourceId === source.id,
        )?.id,
      });
  for (const f of resolution.fields) {
    if (!resolution.visible.has(f.id) && !f.system) continue;
    let v = resolution.values[f.id];
    const fail = (code: string, message: string) =>
      errors.push({
        fieldId: f.id,
        code,
        message: `${f.label}: ${message}`,
        severity: "error",
      });
    if (f.source.kind === "statement") {
      if (v && typeof v === "object") {
        try {
          v = validateStatementSelection(project, f, v);
        } catch {
          fail(
            "STATEMENT_SELECTION",
            "select a statement from the current group version or enter custom text",
          );
        }
      } else if (v != null && typeof v !== "string")
        fail("STATEMENT_TEXT", "enter statement text");
      const custom =
        f.editable &&
        !f.system &&
        Object.hasOwn(input, f.id) &&
        typeof input[f.id] === "string";
      const system = project.parts.find(
        (part) => part.id === partId,
      )?.systemValues;
      if (
        !f.source.datasetId &&
        f.source.statementId &&
        !custom &&
        !(system && Object.hasOwn(system, f.id) && typeof v === "string")
      )
        fail(
          "STATEMENT_UNAVAILABLE",
          "configure the referenced statement group content",
        );
    } else if (v && typeof v === "object" && Object.hasOwn(v, "statement")) {
      fail(
        "STATEMENT_SELECTION",
        "this variable no longer uses the selected statement group",
      );
    }
    if (f.required && isEmpty(v)) {
      fail("REQUIRED", "a value is required");
      continue;
    }
    if (isEmpty(v)) {
      snapshot[f.id] = v;
      continue;
    }
    if (
      f.type === "checkbox" &&
      !f.choices.length &&
      !isDynamicChoiceField(f) &&
      typeof v !== "boolean"
    )
      fail("BOOLEAN", "enter an explicit boolean value");
    const s = displayValue(v);
    if (s.length > 32768) fail("VALUE_LIMIT", "value is too long");
    if (f.validation.maxLength != null && s.length > f.validation.maxLength)
      fail("MAX_LENGTH", `maximum ${f.validation.maxLength} characters`);
    if (
      f.validation.pattern &&
      !safePattern(f.validation.pattern).test(s.slice(0, 32768))
    )
      fail("PATTERN", "does not match the required format");
    if (
      ["dropdown", "radio", "image-list"].includes(f.type) &&
      f.source.kind !== "dam" &&
      !resolution.choices[f.id].some((c) => c.value === s)
    )
      fail("CHOICE", "select an allowed value");
    if (
      f.type === "preset" &&
      !resolution.choices[f.id].some((record) => record.value === s)
    )
      fail("CHOICE", "select an allowed preset");
    if (
      f.type === "image-list" &&
      f.source.kind !== "dam" &&
      project.pages.some((page) =>
        page.nodes.some(
          (node) =>
            node.fieldId === f.id && ["image", "map"].includes(node.type),
        ),
      )
    ) {
      const selected = resolution.choices[f.id].find((c) => c.value === s);
      if (selected && !selected.assetId && !/^asset_[a-zA-Z0-9_-]+$/.test(s))
        fail("IMAGE_CHOICE_UNMAPPED", "no image is assigned to this option");
    }
    if (
      f.type === "checkbox" &&
      (f.choices.length || isDynamicChoiceField(f)) &&
      (!Array.isArray(v) ||
        v.some((x) => !resolution.choices[f.id].some((c) => c.value === x)))
    )
      fail("CHOICE", "select allowed choices");
    if (f.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s))
      fail("EMAIL", "enter a valid email address");
    if (f.type === "color" && !/^#[0-9a-f]{6}$/i.test(s))
      fail("COLOR", "enter a six-digit hex color");
    if (f.type === "number") {
      const num = numericValue(v);
      if (!Number.isFinite(num)) fail("NUMBER", "enter a number");
      else {
        if (f.validation.min != null && num < f.validation.min)
          fail("MIN", `minimum ${f.validation.min}`);
        if (f.validation.max != null && num > f.validation.max)
          fail("MAX", `maximum ${f.validation.max}`);
        v = num;
      }
    }
    if (f.type === "date" && !strictDate(s)) fail("DATE", "enter a valid date");
    if (f.type === "datetime" && !strictDateTime(s))
      fail("DATE", "enter a valid date and time");
    if (f.type === "time" && !strictTime(s)) fail("TIME", "enter a valid time");
    const policyError = policyValueError(f, v);
    if (policyError && !errors.some((error) => error.fieldId === f.id))
      fail("VALIDATION_RULE", policyError);
    if (f.type === "address" && (typeof v !== "object" || Array.isArray(v)))
      fail("ADDRESS", "enter a structured address");
    if (
      f.type === "image" ||
      f.type === "map" ||
      (f.type === "image-list" && f.source.kind === "dam")
    ) {
      if (typeof v === "string") {
        if (!/^asset_/.test(v)) fail("ASSET", "use an uploaded asset");
        else v = { assetId: v, adjustments: defaultAdjustments() };
      } else if (
        v &&
        typeof v === "object" &&
        !Array.isArray(v) &&
        typeof v.assetId === "string"
      ) {
        const parsed = adjustmentSchema.safeParse(v.adjustments || {});
        if (!parsed.success)
          fail("IMAGE_ADJUSTMENT", "invalid adjustment values");
        else {
          const a = parsed.data,
            d = defaultAdjustments();
          for (const key of Object.keys(d) as (keyof typeof d)[]) {
            if (key === "version") continue;
            if (
              JSON.stringify(a[key]) !== JSON.stringify(d[key]) &&
              !f.image.tools.includes(
                key === "width"
                  ? "resize"
                  : key === "rotate"
                    ? "rotate"
                    : (key as (typeof f.image.tools)[number]),
              )
            )
              fail("IMAGE_TOOL_DISABLED", `${key} editing is disabled`);
          }
          if (a.width && !f.image.tools.includes("resize"))
            fail("IMAGE_TOOL_DISABLED", "resize editing is disabled");
          if (
            a.backgroundMaskAssetId &&
            !f.image.tools.includes("removeBackground")
          )
            fail("IMAGE_TOOL_DISABLED", "background removal is disabled");
          let provenance = {};
          if (Object.hasOwn(v, "backgroundRemoval")) {
            const result = backgroundRemovalSchema.safeParse(
              v.backgroundRemoval,
            );
            if (!result.success)
              fail("IMAGE_BACKGROUND", "invalid background removal result");
            else {
              if (!f.image.tools.includes("removeBackground"))
                fail("IMAGE_TOOL_DISABLED", "background removal is disabled");
              provenance = { backgroundRemoval: result.data };
            }
          }
          if (Object.hasOwn(v, "dam")) {
            const receipt = damReceiptSchema.safeParse(v.dam);
            if (!receipt.success)
              fail("DAM_METADATA", "invalid image-library selection");
            else {
              if (
                f.source.kind === "dam" &&
                (receipt.data.providerId !== f.source.providerId ||
                  receipt.data.rootFolderId !== f.source.folderId)
              )
                fail(
                  "DAM_METADATA",
                  "select an image from the current library folder",
                );
              provenance = { ...provenance, dam: receipt.data };
            }
          }
          if (
            f.type === "map" &&
            ["providerId", "location", "attribution"].some((key) =>
              Object.hasOwn(v as object, key),
            )
          ) {
            const metadata = mapProvenanceSchema.safeParse(v);
            if (!metadata.success)
              fail("MAP_METADATA", "invalid map location or attribution");
            else provenance = { ...provenance, ...metadata.data };
          }
          v = { ...provenance, assetId: v.assetId, adjustments: a };
        }
      } else fail("ASSET", "invalid image asset");
    }
    snapshot[f.id] = v;
  }
  resolution.values = { ...resolution.values, ...snapshot };
  return { resolution, errors, snapshot };
}
export function applyPreset(
  project: Project,
  values: Values,
  fieldId: string,
  presetId: string,
  partId?: string,
  context: Context = {},
): Values {
  const fields = effectiveFields(project, partId, context),
    selector = fields.find((f) => f.id === fieldId);
  if (
    !selector ||
    selector.type !== "preset" ||
    !presetWritable(selector) ||
    !resolve(project, values, partId, context).visible.has(fieldId)
  )
    return { ...values };
  const preset = selector.presets.find((p) => p.id === presetId);
  const result = { ...values, [fieldId]: presetId };
  const assignments =
    preset?.values ||
    (presetId === ""
      ? Object.fromEntries(
          selector.presets.flatMap((record) =>
            Object.keys(record.values).map((key) => [key, ""]),
          ),
        )
      : {});
  for (const [key, value] of Object.entries(assignments)) {
    const f = presetTarget(fields, key);
    if (presetWritable(f)) result[f.id] = value;
  }
  return result;
}
export function applyFormProfile(
  project: Project,
  values: Values,
  instanceId: string,
  profile: Values,
  partId?: string,
  context: Context = {},
): Values {
  const i = project.formInstances.find((i) => i.id === instanceId);
  if (!i) throw new Error("Form instance not found");
  const form = project.forms.find((form) => form.id === i.formId);
  if (!form) throw new Error("Form not found");
  const fields = effectiveFields(project, partId, context),
    system = project.parts.find((part) => part.id === partId)?.systemValues;
  const result = { ...values };
  for (const [struct, field] of Object.entries(i.mapping)) {
    const target = fields.find((target) => target.id === field);
    if (
      Object.hasOwn(profile, struct) &&
      form.fields.some((source) => source.id === struct) &&
      target &&
      presetWritable(target) &&
      !target.autoGenerated &&
      !(system && Object.hasOwn(system, field))
    )
      Object.defineProperty(result, field, {
        value: structuredClone(profile[struct]),
        enumerable: true,
        writable: true,
        configurable: true,
      });
  }
  const visible = resolve(project, result, partId, context).visible;
  for (const field of Object.values(i.mapping))
    if (!visible.has(field)) {
      if (Object.hasOwn(values, field)) result[field] = values[field];
      else delete result[field];
    }
  return result;
}
export function formatValue(field: Field | undefined, v: unknown): string {
  let s = displayValue(v);
  if (field?.type === "richtext")
    s = s
      .replace(/<br\s*\/?\s*>|<\/p>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&");
  if (field) s = formattedPolicyValue(field, v, s);
  if (field?.format.uppercase) s = s.toUpperCase();
  return `${field?.format.prefix || ""}${s}${field?.format.suffix || ""}`;
}
export type QrKind = NonNullable<Field["qr"]>["kind"];
export interface QrPart {
  key: string;
  label: string;
  input?:
    | "text"
    | "textarea"
    | "email"
    | "tel"
    | "url"
    | "number"
    | "datetime-local"
    | "select"
    | "checkbox";
  options?: { value: string; label: string }[];
  placeholder?: string;
}
/** Standard QR code types and the information each one encodes. */
export const qrTypes: Record<QrKind, { label: string; parts: QrPart[] }> = {
  website: {
    label: "Website (URL)",
    parts: [
      {
        key: "url",
        label: "Web address",
        input: "url",
        placeholder: "https://example.com",
      },
    ],
  },
  text: {
    label: "Plain text",
    parts: [{ key: "text", label: "Text", input: "textarea" }],
  },
  email: {
    label: "Email",
    parts: [
      { key: "email", label: "Email address", input: "email" },
      { key: "subject", label: "Subject" },
      { key: "body", label: "Message", input: "textarea" },
    ],
  },
  phone: {
    label: "Phone call",
    parts: [{ key: "phone", label: "Phone number", input: "tel" }],
  },
  sms: {
    label: "Text message (SMS)",
    parts: [
      { key: "phone", label: "Phone number", input: "tel" },
      { key: "message", label: "Message", input: "textarea" },
    ],
  },
  contact: {
    label: "Contact card (vCard)",
    parts: [
      { key: "name", label: "Full name" },
      { key: "organization", label: "Organization" },
      { key: "title", label: "Job title" },
      { key: "phone", label: "Phone", input: "tel" },
      { key: "email", label: "Email", input: "email" },
      { key: "url", label: "Website", input: "url" },
      { key: "address", label: "Full address (legacy)" },
      ...addressParts,
    ],
  },
  wifi: {
    label: "Wi-Fi network",
    parts: [
      { key: "ssid", label: "Network name (SSID)" },
      {
        key: "security",
        label: "Security",
        input: "select",
        options: [
          { value: "WPA", label: "WPA/WPA2/WPA3" },
          { value: "WEP", label: "WEP" },
          { value: "nopass", label: "None" },
        ],
      },
      { key: "password", label: "Password" },
      { key: "hidden", label: "Hidden network", input: "checkbox" },
    ],
  },
  location: {
    label: "Map location",
    parts: [
      { key: "latitude", label: "Latitude", input: "number" },
      { key: "longitude", label: "Longitude", input: "number" },
      ...addressParts,
    ],
  },
  event: {
    label: "Calendar event",
    parts: [
      { key: "title", label: "Event title" },
      { key: "start", label: "Starts", input: "datetime-local" },
      { key: "end", label: "Ends", input: "datetime-local" },
      { key: "location", label: "Location" },
      { key: "description", label: "Details", input: "textarea" },
    ],
  },
};
/** Location source choices hide inactive inputs while preserving their saved answers. */
export function qrParts(f: Field): QrPart[] {
  const disabled = f.qr?.disabledParts || [];
  const parts = (qrTypes[f.qr?.kind || "website"]?.parts || []).filter(
    (part) => !disabled.includes(part.key),
  );
  if (f.qr?.kind !== "location") return parts;
  const mode = disabled.some((key) => ["latitude", "longitude"].includes(key))
    ? "address"
    : f.qr.locationMode;
  if (!mode || mode === "auto") return parts;
  return parts.filter((part) =>
    mode === "coordinates"
      ? ["latitude", "longitude"].includes(part.key)
      : !["latitude", "longitude"].includes(part.key),
  );
}
/** Parts a person fills in on the form (not mapped to a field, not fixed). */
export function qrAskedParts(f: Field): QrPart[] {
  return qrParts(f).filter(
    (part) => !f.qr?.mapping[part.key] && f.qr?.fixed?.[part.key] === undefined,
  );
}
/** Linked and fixed answers take precedence; editable defaults apply until overridden. */
export function qrPartValue(
  f: Field,
  values: Values,
  key: string,
  answers?: Record<string, unknown>,
): unknown {
  if (f.qr?.disabledParts?.includes(key)) return undefined;
  const mapped = f.qr?.mapping[key];
  if (mapped) return values[mapped];
  if (f.qr?.fixed?.[key] !== undefined) return f.qr.fixed[key];
  if (answers?.[key] !== undefined) return answers[key];
  const source = f.qr?.defaults?.[key];
  if (source) return values[source];
  const own = values[f.id];
  return !answers &&
    key === qrTypes[f.qr?.kind || "website"].parts[0]?.key &&
    typeof own === "string"
    ? own
    : undefined;
}
export function qrPayload(f: Field, values: Values): string {
  const own = values[f.id];
  const answers =
    own && typeof own === "object" && !Array.isArray(own)
      ? (own as Record<string, unknown>)
      : undefined;
  const kind =
    f.qr?.kind ||
    (answers?.text !== undefined && answers.url === undefined
      ? "text"
      : "website");
  // Mapped field → fixed answer → the person's answer in this QR's own form.
  const get = (key: string) => {
    return displayValue(qrPartValue(f, values, key, answers));
  };
  const fallback = answers ? "" : displayValue(own);
  switch (kind) {
    case "text":
      return get("text") || fallback;
    case "website":
      return get("url") || fallback;
    case "email": {
      const query = [
        ["subject", get("subject")],
        ["body", get("body")],
      ]
        .filter(([, v]) => v)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join("&");
      return `mailto:${get("email") || fallback}${query ? `?${query}` : ""}`;
    }
    case "phone":
      return `tel:${get("phone") || fallback}`;
    case "sms":
      return `SMSTO:${get("phone") || fallback}:${get("message")}`;
    case "location":
      if (
        f.qr?.disabledParts?.some((key) =>
          ["latitude", "longitude"].includes(key),
        ) ||
        f.qr?.locationMode === "address" ||
        (f.qr?.locationMode !== "coordinates" &&
          !get("latitude") &&
          !get("longitude"))
      ) {
        const address = addressParts
          .map(({ key }) => get(key).trim())
          .filter(Boolean)
          .join(", ");
        return address
          ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
          : "";
      }
      if (
        !get("latitude").trim() ||
        !get("longitude").trim() ||
        !Number.isFinite(Number(get("latitude"))) ||
        !Number.isFinite(Number(get("longitude"))) ||
        Math.abs(Number(get("latitude"))) > 90 ||
        Math.abs(Number(get("longitude"))) > 180
      )
        return "";
      return `geo:${get("latitude")},${get("longitude")}`;
    case "wifi": {
      const esc = (s: string) => s.replace(/([\\;,:"])/g, "\\$1");
      const security = get("security") || "WPA";
      const hidden = ["true", "yes", "1"].includes(get("hidden").toLowerCase());
      return `WIFI:T:${security};S:${esc(get("ssid"))};${security === "nopass" ? "" : `P:${esc(get("password"))};`}${hidden ? "H:true;" : ""};`;
    }
    case "event": {
      const esc = (s: string) =>
        s.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/[;,]/g, "\\$&");
      const stamp = (s: string) =>
        s.replace(/[-:]/g, "").replace(/\.\d+/, "").slice(0, 15);
      return [
        "BEGIN:VEVENT",
        `SUMMARY:${esc(get("title"))}`,
        get("start") && `DTSTART:${stamp(get("start"))}`,
        get("end") && `DTEND:${stamp(get("end"))}`,
        get("location") && `LOCATION:${esc(get("location"))}`,
        get("description") && `DESCRIPTION:${esc(get("description"))}`,
        "END:VEVENT",
      ]
        .filter(Boolean)
        .join("\r\n");
    }
    case "contact": {
      const esc = (s: string) =>
        s.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/[;,]/g, "\\$&");
      return [
        "BEGIN:VCARD",
        "VERSION:3.0",
        `FN:${esc(get("name"))}`,
        `ORG:${esc(get("organization"))}`,
        `TITLE:${esc(get("title"))}`,
        `TEL:${esc(get("phone"))}`,
        `EMAIL:${esc(get("email"))}`,
        `URL:${esc(get("url"))}`,
        ...(get("address") || addressParts.some(({ key }) => get(key))
          ? [
              `ADR:;${esc(get("address2"))};${esc(get("address1") || get("address"))};${esc(get("city"))};${esc(get("state"))};${esc(get("postalCode"))};${esc(get("country"))}`,
            ]
          : []),
        "END:VCARD",
      ].join("\r\n");
    }
    default:
      return fallback;
  }
}
export function deleteField(project: Project, id: string): Project {
  if (
    project.pages.some((page) =>
      page.nodes.some(
        (node) =>
          node.textCalculation &&
          calculationDependencies(node.textCalculation).includes(id),
      ),
    )
  )
    throw new Error(
      "Rebind calculated text layers before deleting their source",
    );
  const references = (field: Field) =>
    field.id !== id &&
    field.calculation &&
    calculationDependencies(field.calculation).includes(id);
  if (
    project.fields.some(references) ||
    project.parts.some((part) =>
      project.fields.some((field) =>
        references(
          fieldSchema.parse({ ...field, ...part.overrides[field.id] }),
        ),
      ),
    )
  )
    throw new Error("Rebind calculated variables before deleting their source");
  if (
    project.fields.some(
      (f) => f.source.kind === "lookup" && f.source.lookupFieldId === id,
    ) ||
    project.parts.some((part) =>
      Object.values(part.overrides).some(
        (patch) =>
          patch.source?.kind === "lookup" && patch.source.lookupFieldId === id,
      ),
    )
  )
    throw new Error("Rebind lookup variables before deleting their selector");
  const p = structuredClone(project);
  const removedKeys = [id, project.fields.find((f) => f.id === id)?.key].filter(
    (key): key is string => !!key,
  );
  p.fields = p.fields.filter((f) => f.id !== id);
  for (const page of p.pages)
    for (const n of page.nodes) {
      if (n.fieldId === id) delete n.fieldId;
      if (n.textTemplate)
        n.textTemplate = n.textTemplate.map((part) =>
          "fieldId" in part && part.fieldId === id ? { text: "" } : part,
        );
    }
  for (const part of p.parts) {
    delete part.overrides[id];
    delete part.systemValues[id];
  }
  p.triggers = p.triggers
    .filter((t) => t.sourceId !== id)
    .map((t) => ({
      ...t,
      show: t.show.filter((x) => x !== id),
      hide: t.hide.filter((x) => x !== id),
      clear: t.clear.filter((x) => x !== id),
      exclude: Object.fromEntries(
        Object.entries(t.exclude).filter(([k]) => k !== id),
      ),
    }));
  const prune = (c?: Condition): Condition | undefined => {
    if (!c || c.field === id) return undefined;
    if (c.field) return c;
    const key = c.all ? "all" : "any";
    const children = (c[key] || [])
      .map(prune)
      .filter((x): x is Condition => !!x);
    return children.length ? { [key]: children } : undefined;
  };
  for (const f of p.fields) {
    f.visibleWhen = prune(f.visibleWhen);
    if (f.qr)
      f.qr.mapping = Object.fromEntries(
        Object.entries(f.qr.mapping).filter(([, v]) => v !== id),
      );
    if (f.qr?.defaults)
      f.qr.defaults = Object.fromEntries(
        Object.entries(f.qr.defaults).filter(([, v]) => v !== id),
      );
    if (f.map?.mapping)
      f.map.mapping = Object.fromEntries(
        Object.entries(f.map.mapping).filter(([, v]) => v !== id),
      );
    for (const key of ["defaults", "linked"] as const)
      if (f.map?.[key])
        f.map[key] = Object.fromEntries(
          Object.entries(f.map[key]!).filter(([, v]) => v !== id),
        );
    for (const preset of f.presets) {
      if (preset.match?.field === id) delete preset.match;
      for (const key of removedKeys) delete preset.values[key];
    }
  }
  for (const part of p.parts)
    for (const patch of Object.values(part.overrides))
      for (const record of patch.presets || []) {
        if (record.match?.field === id) delete record.match;
        for (const key of removedKeys) delete record.values[key];
      }
  for (const page of p.pages)
    for (const n of page.nodes) n.visibleWhen = prune(n.visibleWhen);
  for (const page of p.pages)
    for (const group of page.layoutGroups || [])
      group.visibleWhen = prune(group.visibleWhen);
  for (const i of p.formInstances)
    i.mapping = Object.fromEntries(
      Object.entries(i.mapping).filter(([, v]) => v !== id),
    );
  return p;
}
